import React from 'react';

import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Popover,
  SegmentedControl,
  Skeleton,
  Text,
  Tooltip,
} from '@mantine/core';
import { DatePicker, DateValue } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconCalendar,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { WorkoutCalendarKiloClassType } from 'types/generated/models/WorkoutCalendarKiloClassType';

export type WhiteboardTab = 'whiteboard' | 'results';

interface WhiteboardClassNavProps {
  classTypes: WorkoutCalendarKiloClassType[];
  selectedTypeId: string | null;
  onTypeSelect: (typeId: string) => void;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  isLoading: boolean;
  activeTab: WhiteboardTab;
  onTabChange: (tab: WhiteboardTab) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  showFullscreenButton: boolean;
}

const WhiteboardClassNav = ({
  classTypes,
  selectedTypeId,
  onTypeSelect,
  selectedDate,
  onDateChange,
  isLoading,
  activeTab,
  onTabChange,
  isFullscreen,
  onToggleFullscreen,
  showFullscreenButton,
}: WhiteboardClassNavProps): JSX.Element => {
  const isToday = dayjs(selectedDate).isSame(dayjs(), 'day');
  const selectedType = classTypes.find(t => t.kiloClassTypeId === selectedTypeId);

  const classLabel = selectedType ? selectedType.name : 'Select class';

  const [datePickerOpened, { open: openDatePicker, close: closeDatePicker }] = useDisclosure(false);

  const handleDatePickerChange = (date: DateValue): void => {
    if (date) {
      onDateChange(date);
      closeDatePicker();
    }
  };

  return (
    <Group position='apart' noWrap>
      <Group spacing='xs' noWrap>
        <Text size='lg' weight={600}>
          Whiteboard
        </Text>

        <ActionIcon
          variant='subtle'
          onClick={() => onDateChange(dayjs(selectedDate).subtract(1, 'day').toDate())}
        >
          <IconChevronLeft size={16} />
        </ActionIcon>

        <Popover
          opened={datePickerOpened}
          onClose={closeDatePicker}
          closeOnEscape
          position='bottom'
          shadow='md'
          radius='md'
          withinPortal
        >
          <Popover.Target>
            <Group spacing={6} noWrap onClick={openDatePicker} sx={{ cursor: 'pointer' }}>
              <IconCalendar size={16} color='#868e96' />
              <Text size='sm' weight={500}>
                {dayjs(selectedDate).format('ddd, MMM D, YYYY')}
              </Text>
            </Group>
          </Popover.Target>
          <Popover.Dropdown>
            <DatePicker value={selectedDate} onChange={handleDatePickerChange} />
          </Popover.Dropdown>
        </Popover>

        <Badge
          color={isToday ? 'green' : 'gray'}
          variant={isToday ? 'filled' : 'outline'}
          sx={{ cursor: 'pointer' }}
          onClick={() => onDateChange(new Date())}
        >
          Today
        </Badge>

        <ActionIcon
          variant='subtle'
          onClick={() => onDateChange(dayjs(selectedDate).add(1, 'day').toDate())}
        >
          <IconChevronRight size={16} />
        </ActionIcon>

        {isLoading ? (
          <Skeleton width={200} height={36} radius='sm' />
        ) : (
          <Menu position='bottom-start' withinPortal>
            <Menu.Target>
              <Button variant='default' size='sm' rightIcon={<IconChevronDown size={14} />}>
                {classLabel}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {classTypes.length === 0 ? (
                <Menu.Item disabled>No classes for this day</Menu.Item>
              ) : (
                classTypes.map(type => (
                  <Menu.Item
                    key={type.kiloClassTypeId}
                    onClick={() => onTypeSelect(type.kiloClassTypeId)}
                    sx={{ fontWeight: type.kiloClassTypeId === selectedTypeId ? 600 : 400 }}
                  >
                    <Text size='sm'>{type.name}</Text>
                    {!type.workout && (
                      <Text size='xs' color='dimmed'>
                        No workout assigned
                      </Text>
                    )}
                  </Menu.Item>
                ))
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <Group spacing='xs' noWrap>
        <SegmentedControl
          value={activeTab}
          onChange={value => onTabChange(value as WhiteboardTab)}
          size='sm'
          data={[
            { label: 'Workout Details', value: 'whiteboard' },
            { label: 'Workout Results', value: 'results' },
          ]}
        />

        {showFullscreenButton && (
          <Tooltip
            label='In fullscreen, only Athlete Notes are shown. Coach Notes are hidden for security.'
            withArrow
            multiline
            width={260}
            withinPortal
            zIndex={400}
          >
            <Button
              size='sm'
              leftIcon={isFullscreen ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
              onClick={onToggleFullscreen}
            >
              Fullscreen
            </Button>
          </Tooltip>
        )}
      </Group>
    </Group>
  );
};

export default WhiteboardClassNav;
